// Reference plane under the connection, as in the lock calculator (port of its SimpleGrid): an unlit white floor
// ("the sheet of paper"), its border and a grid of 100 mm squares on the horizontal plane, touching the lowest point
// of the scene. The sheet is centred under the scene on its own squares, so the lines keep their place while the
// connection changes.
import { memo, useEffect, useMemo, type FC } from "react";
import * as THREE from "three";
import { GRID } from "./viewerConfig";
import { MM3D, type SceneLayout } from "../view3dScene";

export type ScrewGridProps = {
  // the whole scene's visible geometry (not empty)
  bounds: THREE.Box3;
  layout: SceneLayout;
};

const ScrewGrid: FC<ScrewGridProps> = ({ bounds, layout }) => {
  const { size, divisions, center } = useMemo(() => gridLayout(bounds, layout), [bounds, layout]);

  const lines = useMemo(() => {
    const helper = new THREE.GridHelper(size, divisions, GRID.lineColor, GRID.lineColor);
    helper.material.transparent = true;
    helper.material.opacity = GRID.lineOpacity;
    return helper;
  }, [size, divisions]);
  useEffect(() => () => lines.dispose(), [lines]);

  // only the sheet's outline (4 sides), not one per square
  const border = useMemo(() => {
    const plane = new THREE.PlaneGeometry(size, size);
    const edges = new THREE.EdgesGeometry(plane);
    plane.dispose();
    return edges;
  }, [size]);
  useEffect(() => () => border.dispose(), [border]);

  return (
    <group position={center}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -GRID.floorDrop, 0]}>
        <planeGeometry args={[size, size]} />
        <meshBasicMaterial color={GRID.floorColor} />
      </mesh>
      <lineSegments geometry={border} rotation={[-Math.PI / 2, 0, 0]} position={[0, -GRID.borderDrop, 0]}>
        <lineBasicMaterial color={GRID.borderColor} />
      </lineSegments>
      <primitive object={lines} />
    </group>
  );
};

// Side: the minimum sheet, or twice the scene's horizontal extent; an even number of squares, so the centre snapped
// to the squares stays on a grid line. Height: element 2 bottom base (yBot).
function gridLayout(bounds: THREE.Box3, layout: SceneLayout): { size: number; divisions: number; center: [number, number, number] } {
  const center = bounds.getCenter(new THREE.Vector3());
  const extent = bounds.getSize(new THREE.Vector3());
  const squares = Math.ceil(Math.max(GRID.minSize, 2 * Math.max(extent.x, extent.z)) / GRID.cell);
  const divisions = squares + (squares % 2);
  const snap = (value: number) => Math.round(value / GRID.cell) * GRID.cell;
  const gridY = layout.yBot * MM3D;
  return { size: divisions * GRID.cell, divisions, center: [snap(center.x), gridY, snap(center.z)] };
}

export default memo(ScrewGrid);
